import React, { Component, Fragment } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { compose } from 'recompose';
import { Trans } from 'react-i18next';
import { MutationFunction, MutationResult } from 'react-apollo';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloError } from 'apollo-client';
import { MenuItem } from '@material-ui/core';

import { GroupIdApprovalInput, UpdateContentApprovalsComponent } from 'api';
import { Alert } from 'components';
import { index as indexPath, content as contentPath } from 'paths';
import { contentCollection } from 'apollo/reducers';
import { updateContentApprovalsResponse } from 'apollo/responses';
import {
  withQueryParams,
  withContent,
  withNotify,
  IWithQueryParams,
  IWithContent,
  IWithNotify,
} from 'providers';

interface IKeys {
  alertMessage: string;
  alertConfirm: string;
  menuItem: string;
  notify: string;
  values?: object;
}

interface IProps {
  groupIdApprovals: GroupIdApprovalInput[];
  i18nKeys: IKeys;
  onError: (error: ApolloError) => void;
}

interface IPropsEnhanced
  extends IProps,
    IWithQueryParams,
    IWithContent,
    IWithNotify,
    RouteComponentProps {}

interface IState {
  open: boolean;
}

const enhancers = compose<IPropsEnhanced, IProps>(
  withContent,
  withQueryParams,
  withRouter,
  withNotify,
);

class GroupApprovals extends Component<IPropsEnhanced, IState> {
  state: IState = {
    open: false,
  };

  redirectHomeIfNeeded() {
    const { history, match } = this.props;
    if (match.path === contentPath) {
      history.push(indexPath);
    }
  }

  handlePostDelete = (updateContentApprovals: MutationFunction) => () => {
    const { content, groupIdApprovals } = this.props;
    const { content_id } = content;

    const options = {
      optimisticResponse: updateContentApprovalsResponse(),
      update: this.deletePost(content_id),
      variables: {
        content_id,
        group_id_approvals: groupIdApprovals,
      },
    };

    return updateContentApprovals(options);
  };

  toggleConfirm = () => {
    const { open } = this.state;
    this.setState({ open: !open });
  };

  deletePost = (contentId: string) => (cache: InMemoryCache, result: MutationResult) => {
    const { i18nKeys, notify, query, variables } = this.props;

    notify.info({ i18n: { i18nKey: i18nKeys.notify, values: i18nKeys.values } });

    contentCollection({
      name: 'removePost',
      cache,
      result,
      query,
      variables,
      contentId,
    });

    this.redirectHomeIfNeeded();
  };

  renderAlert(updateContentApprovals: MutationFunction) {
    const { i18nKeys } = this.props;
    const { open } = this.state;

    return (
      <Alert
        open={open}
        onConfirm={this.handlePostDelete(updateContentApprovals)}
        onCancel={this.toggleConfirm}
        message={<Trans i18nKey={i18nKeys.alertMessage} values={i18nKeys.values} />}
        confirm={<Trans i18nKey={i18nKeys.alertConfirm} values={i18nKeys.values} />}
      />
    );
  }

  render() {
    const { i18nKeys, onError } = this.props;

    return (
      <UpdateContentApprovalsComponent onError={onError}>
        {updateContentApprovals => (
          <Fragment>
            {this.renderAlert(updateContentApprovals)}
            <MenuItem onClick={this.toggleConfirm}>
              <Trans i18nKey={i18nKeys.menuItem} values={i18nKeys.values} />
            </MenuItem>
          </Fragment>
        )}
      </UpdateContentApprovalsComponent>
    );
  }
}

export default enhancers(GroupApprovals);
